import { useState, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, User } from 'lucide-react';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { GoogleAuthButton, AppleAuthButton } from '@/components/auth/OAuthButtons';
import {
  TextField,
  PasswordField,
  OrDivider,
  FormError,
  SubmitButton,
} from '@/components/auth/FormFields';
import { register, readApiError } from '@/lib/auth-api';

export function SignupPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [agree, setAgree] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!agree) {
      setError(t('auth.error_must_accept_terms'));
      return;
    }
    if (password.length < 8) {
      setError(t('auth.error_password_too_short'));
      return;
    }
    setLoading(true);
    try {
      await register({
        email,
        password,
        displayName,
        locale: (i18n.language || 'en').split('-')[0],
      });
      // User is logged in immediately — verification reminder shows as a
      // banner inside the app shell instead of gating /verify-email.
      navigate('/onboarding', { replace: true });
    } catch (err) {
      setError(readApiError(err, t('auth.error_signup_failed')));
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthLayout
      title={t('auth.signup_title')}
      subtitle={t('auth.signup_subtitle')}
      footer={
        <>
          {t('auth.have_account')}{' '}
          <Link to="/login" className="font-semibold text-accent-700 hover:underline">
            {t('auth.login_link')}
          </Link>
        </>
      }
    >
      <div className="space-y-3">
        <GoogleAuthButton className="w-full" />
        <AppleAuthButton className="w-full" />
      </div>

      <div className="my-6">
        <OrDivider label={t('auth.or_signup_with_email')} />
      </div>

      <form className="space-y-4" onSubmit={submit} noValidate>
        <TextField
          label={t('auth.display_name')}
          autoComplete="name"
          required
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          leadingIcon={<User className="h-4 w-4" />}
        />
        <TextField
          label={t('auth.email')}
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          leadingIcon={<Mail className="h-4 w-4" />}
        />
        <PasswordField
          label={t('auth.password')}
          autoComplete="new-password"
          required
          showStrength
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          hint={t('auth.password_hint')}
        />

        <label className="flex items-start gap-2 text-xs text-ink-600 leading-relaxed">
          <input
            type="checkbox"
            checked={agree}
            onChange={(e) => setAgree(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-ink-300 text-accent-500 focus:ring-accent-500/30"
          />
          <span>
            {t('auth.agree_prefix')}{' '}
            <Link to="/terms" className="font-medium text-accent-700 hover:underline">
              {t('auth.terms')}
            </Link>{' '}
            {t('common.and')}{' '}
            <Link to="/privacy" className="font-medium text-accent-700 hover:underline">
              {t('auth.privacy')}
            </Link>
            .
          </span>
        </label>

        <FormError message={error} />

        <SubmitButton loading={loading}>{t('auth.signup_submit')}</SubmitButton>

        <p className="text-center text-xs text-ink-500">{t('auth.free_tier_note')}</p>
      </form>
    </AuthLayout>
  );
}
